import React, { useEffect } from "react";
import TableRow from "@mui/material/TableRow";
import { TopicGroup } from "../models";
import TableCell from "@mui/material/TableCell";
import Checkbox from "@mui/material/Checkbox";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import TopicRow from "./topicRow";
import EmptyCheckBoxCell from "./common/emptyCheckboxCell";
import { Box, Grid, Typography } from "@mui/material";
import { MAX_FIRST_CELL_WIDTH, TOTAL_NUM_OF_COLUMNS } from "../styles/constants";
import MoreTopicGroupOptions from "./buttons/moreTopicGroupOptions";
import TopicCreation from "./dialogs/topicCreation";
import TopicGroupRename from "./dialogs/topicGroupRename";

const TopicGroupRow: React.FunctionComponent<ComponentProps> = (props) => {
    const [checked, setChecked] = React.useState(false);
    const [topicsCheckedCount, setTopicsCheckedCount] = React.useState(0);

    const [createTopicDialog, setCreateTopicDialog] = React.useState(false);
    const [editGroupDialog, setEditGroupDialog] = React.useState(false);

    const children = props.topicGroup.tags;

    const onCloseEditGroup = () => setEditGroupDialog(false);

    const onOpenEditDialog = () => setEditGroupDialog(true);

    const onCloseCreateDialog = () => setCreateTopicDialog(false);

    const onOpenCreateDialog = () => setCreateTopicDialog(true);

    const toggleExpand = () => props.onToggleExpand(!props.expanded, props.topicGroup.id);

    const onChangeCheck = () => {
        if (children.length > 0) props.onCheckGroup(!checked, children.map(t => t.id));

        if (children.length === 0) props.onCheckEmptyGroup(!checked, props.topicGroup);
        setChecked(!checked);
    };

    useEffect(() => {
        const handleGroupUpdate = () => {
            const childrenCheckedCount = children.map(t => t.id).filter(val => props.topicsChecked.includes(val)).length;

            if (childrenCheckedCount === children.length) {
                setChecked(true);
            }
            else if (props.topicsChecked.length === 0) {
                setChecked(false);
            }
            setTopicsCheckedCount(childrenCheckedCount);
        };

        if (children.length !== 0) handleGroupUpdate();
    }, [props.topicsChecked, children]);

    useEffect(() => {
        if (children.length === 0) setChecked(props.toggleCheckAll);
    }, [props.toggleCheckAll, children.length]);

    const onCheckTopic = (checked: boolean, topicId: string) => {
        props.onCheckRow(checked, topicId);
        const newCount = checked ? topicsCheckedCount + 1 : topicsCheckedCount - 1;
        if (checked) {
            if (newCount === props.topicGroup.tags.length && newCount !== 0) {
                setChecked(true);
            }
        }
        else {
            if (props.topicGroup.tags.length !== 0 && newCount === 0) {
                setChecked(false);
            }
        }
        setTopicsCheckedCount(newCount);
    };

    const getEmptyTopicGroup = (): JSX.Element => {
        return (
            <TableRow>
                <EmptyCheckBoxCell />
                <TableCell colSpan={TOTAL_NUM_OF_COLUMNS}>
                    <Typography color="grey" fontSize="14px">
                        <i> No topics in topic group </i>
                    </Typography>
                </TableCell>
            </TableRow>
        );
    };

    const getRowsInGroup = () => {
        const topicsInGroup = props.topicGroup.tags;

        return (
            topicsInGroup.length !== 0 ?
                topicsInGroup.map((topic) =>
                    <TopicRow
                        key={topic.id}
                        topic={topic}
                        checked={props.topicsChecked.includes(topic.id)}
                        onCheckRow={onCheckTopic}
                    />
                ) : getEmptyTopicGroup()
        );
    };

    return (
        <React.Fragment>
            <TableRow
                hover
                key={props.topicGroup.id}
                role="checkbox"
                tabIndex={-1}
                aria-checked={checked}
                selected={checked}
            >

                <TableCell colSpan={2}>

                    <Grid container
                        wrap="nowrap"
                        alignItems="center">

                        <Grid item padding="checkbox">
                            <Checkbox
                                color="primary"
                                checked={checked}
                                onClick={onChangeCheck}
                                indeterminate={topicsCheckedCount > 0 && topicsCheckedCount < props.topicGroup.tags.length}
                            />
                        </Grid>

                        <Grid item
                            xs
                            zeroMinWidth
                            maxWidth={MAX_FIRST_CELL_WIDTH / 2}
                            onClick={toggleExpand}
                        >

                            <Box maxWidth={MAX_FIRST_CELL_WIDTH} justifyContent={"start"}>
                                <Typography
                                    color="primary"
                                    fontWeight="500"
                                    textTransform={"uppercase"}
                                    sx={{ WebkitLineClamp: 3 }}
                                    className="truncated-text">
                                    {props.topicGroup.name}
                                </Typography>
                            </Box>

                        </Grid>
                    </Grid>

                </TableCell>


                <TableCell colSpan={TOTAL_NUM_OF_COLUMNS - 3} onClick={toggleExpand} className="topic-group-row">
                    <MoreTopicGroupOptions
                        topicGroup={props.topicGroup}
                        createTopicDialog={onOpenCreateDialog}
                        editGroupDialog={onOpenEditDialog}
                    />
                </TableCell>
                <TopicGroupRename show={editGroupDialog} onClose={onCloseEditGroup} group={props.topicGroup}></TopicGroupRename>
                <TopicCreation show={createTopicDialog} onClose={onCloseCreateDialog} defaultGroup={props.topicGroup} />

                <TableCell align="right" className="topic-group-row">
                    <IconButton
                        size="small"
                        onClick={toggleExpand}
                    >
                        {props.expanded ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                </TableCell>
            </TableRow>

            {props.expanded ? getRowsInGroup() : null}

        </React.Fragment>
    );
};

interface ComponentProps {
    topicGroup: TopicGroup;
    topicsChecked: string[];
    expanded: boolean;
    toggleCheckAll: boolean;
    onCheckRow: (checked: boolean, topicId: string) => void;
    onCheckGroup: (checked: boolean, topics: string[]) => void;
    onCheckEmptyGroup: (checked: boolean, topics: TopicGroup) => void;
    onToggleExpand: (expanded: boolean, groupId: string) => void;
}

export default TopicGroupRow;